import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import BranchAdminCard from "@/components/app/BranchAdminCard";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { usePublicWebSocket } from "@/hooks/usePublicWebSocket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Check, Clock, Warning, User, FolderSimple,
  CurrencyGbp, ChatCircle, FileText,
  CaretRight, Bank, Receipt, DownloadSimple, Bell, SignOut,
  MagnifyingGlass, ArrowSquareOut, Palette, Image as ImageIcon, X as XIcon, Trash, ArrowsClockwise, Buildings, Sparkle,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function formatCurrency(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);
}

export default function ReferrerDashboardPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("cases");
  const [quoteSearch, setQuoteSearch] = useState("");
  const [selectedQuoteIds, setSelectedQuoteIds] = useState(new Set());
  const [deletingQuotes, setDeletingQuotes] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [deletingDocs, setDeletingDocs] = useState(false);
  const [profileForm, setProfileForm] = useState({
    bank_name: "", account_name: "", sort_code: "", account_number: "", vat_number: "", payment_notes: "",
  });
  const [paymentOverride, setPaymentOverride] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  // Mine vs All scope toggle. Defaults to "all" so the broker sees the
  // company-wide pipeline on first load; can drill back to "mine" with one click.
  const [scope, setScope] = useState("all");
  // Branch settings sheet — slides in the branch-admin self-service UI
  // when the referrer clicks the gear button in the header. Renders only
  // when this referrer admins at least one branch.
  const [branchSheetOpen, setBranchSheetOpen] = useState(false);

  // Deep-link from welcome email: ?tab=profile&focus=branding switches to the
  // Profile tab and scrolls the branding card into view on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "profile") setActiveTab("profile");
  }, []);
  useEffect(() => {
    if (activeTab !== "profile") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("focus") !== "branding") return;
    const el = document.querySelector('[data-testid="self-branding-card"]');
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [activeTab]);

  const headers = { "x-referrer-token": token };

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await API.get(`/referrer-dashboard/${token}`);
      setData(d);
      setProfileForm({
        bank_name: d.referrer.bank_name || "",
        account_name: d.referrer.account_name || "",
        sort_code: d.referrer.sort_code || "",
        account_number: d.referrer.account_number || "",
        vat_number: d.referrer.vat_number || "",
        payment_notes: d.referrer.payment_notes || "",
      });
      setPaymentOverride(!!d.referrer.payment_details_override);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired link");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Public dashboard-level WebSocket — refresh on relevant events
  const handleWsMessage = useCallback((msg) => {
    if ([
      "stage_updated", "stages_updated",
      "document_uploaded", "documents_bulk_uploaded",
      "note_created", "referrer_note_created",
      "referrer_document_uploaded",
    ].includes(msg.type)) {
      fetchData();
    }
  }, [fetchData]);
  usePublicWebSocket(token ? `/api/ws/referrer-dashboard/${token}` : null, handleWsMessage);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await API.put("/referrer-profile", { ...profileForm, override: paymentOverride }, { headers });
      toast.success(paymentOverride ? "Saved as your personal payment details" : "Saved as company default — applied to colleagues");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSavingProfile(false);
    }
  };

  // Quote selection / delete (referrer self-serve). Backend enforces:
  //   - referrer_id matches the access_token
  //   - the quote isn't already converted to a case (instructed/linked_case_id)
  // so even if the UI somehow lets a converted quote into the selection, the
  // server will skip it and the toast will report the skipped count.
  const toggleQuoteSelection = (id) =>
    setSelectedQuoteIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const isQuoteDeletable = (q) => !q.instructed && !q.linked_case_id;

  const handleDeleteSingleQuote = async (q, e) => {
    if (e) e.stopPropagation();
    if (!isQuoteDeletable(q)) {
      toast.error("Already converted to a case — contact your solicitor");
      return;
    }
    if (!window.confirm(`Move quote for "${q.client_name}" to recently deleted? Your solicitor can still restore it.`)) return;
    setDeletingQuotes(true);
    try {
      await API.delete(`/quotes/${q.id}`, { headers });
      toast.success("Quote deleted");
      setSelectedQuoteIds((prev) => { const n = new Set(prev); n.delete(q.id); return n; });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete quote");
    } finally {
      setDeletingQuotes(false);
    }
  };

  const handleBulkDeleteQuotes = async () => {
    const ids = Array.from(selectedQuoteIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Move ${ids.length} quote${ids.length === 1 ? "" : "s"} to recently deleted?`)) return;
    setDeletingQuotes(true);
    try {
      const { data } = await API.post("/quotes/bulk-delete", { ids }, { headers });
      const skipped = data?.skipped || 0;
      const deleted = data?.deleted ?? 0;
      if (deleted > 0) {
        toast.success(skipped > 0
          ? `${deleted} deleted · ${skipped} skipped (already converted to a case)`
          : `${deleted} quote${deleted === 1 ? "" : "s"} deleted`);
      } else {
        toast.error("Nothing was deleted — selected quotes may already be converted to cases");
      }
      setSelectedQuoteIds(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete quotes");
    } finally {
      setDeletingQuotes(false);
    }
  };

  // Document selection / delete (referrer self-serve). Backend strict rule:
  // referrer can only delete docs whose `uploaded_by` matches the referrer
  // name. The UI mirrors that rule by greying out docs uploaded by anyone
  // else (e.g. the solicitor or the firm).
  const isDocOwn = (d) => d.uploaded_by === referrer?.name;
  const toggleDocSelection = (id) =>
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleDeleteSingleDoc = async (d) => {
    if (!isDocOwn(d)) {
      toast.error("You can only delete documents you uploaded yourself");
      return;
    }
    if (!window.confirm(`Delete "${d.original_filename}"?`)) return;
    setDeletingDocs(true);
    try {
      await API.delete(`/referrer-documents/${d.case_id}/${d.id}`, { headers });
      toast.success("Document deleted");
      setSelectedDocIds((prev) => { const n = new Set(prev); n.delete(d.id); return n; });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete document");
    } finally {
      setDeletingDocs(false);
    }
  };

  const handleBulkDeleteDocs = async () => {
    const ids = Array.from(selectedDocIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} document${ids.length === 1 ? "" : "s"}?`)) return;
    setDeletingDocs(true);
    try {
      // Group by case_id (backend endpoint is per-case) and call one bulk
      // delete per case. Almost always a single call.
      const byCase = ids.reduce((acc, id) => {
        const d = documents.find((x) => x.id === id);
        if (!d) return acc;
        (acc[d.case_id] = acc[d.case_id] || []).push(id);
        return acc;
      }, {});
      let totalDeleted = 0;
      let totalSkipped = 0;
      for (const [caseId, caseIds] of Object.entries(byCase)) {
        const { data } = await API.post(`/referrer-documents/${caseId}/bulk-delete`, { ids: caseIds }, { headers });
        totalDeleted += data?.deleted ?? 0;
        totalSkipped += data?.skipped ?? 0;
      }
      if (totalDeleted > 0) {
        toast.success(totalSkipped > 0
          ? `${totalDeleted} deleted · ${totalSkipped} skipped (uploaded by someone else)`
          : `${totalDeleted} document${totalDeleted === 1 ? "" : "s"} deleted`);
      } else {
        toast.error("Nothing was deleted — selected documents weren't yours to delete");
      }
      setSelectedDocIds(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete documents");
    } finally {
      setDeletingDocs(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex justify-center py-20"><div className="w-8 h-8 border-2 border-slate-300 border-t-[#100f5d] rounded-full animate-spin" /></div>;
  if (error || !data) return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center"><Warning weight="fill" className="w-12 h-12 text-slate-300 mb-3" /><p className="text-sm text-slate-500">{error || "Not found"}</p></div>;

  const { referrer, cases: rawCases, quotes: rawQuotes, notes, documents } = data;
  const rawInvoices = data.invoices || [];
  const companyMembers = data.company_members || [];
  const showScopeToggle = !!data.company && companyMembers.length > 1;
  // When the broker is solo, force "mine" so the toggle stays hidden and the
  // legacy stat blocks stay 1-for-1 with their previous behaviour.
  const effectiveScope = showScopeToggle ? scope : "mine";
  const filterByScope = (arr) => (
    effectiveScope === "all" ? arr : arr.filter((x) => !x._company_view)
  );
  const cases = filterByScope(rawCases);
  const quotes = filterByScope(rawQuotes);
  const invoices = filterByScope(rawInvoices);
  const fee = (effectiveScope === "all" && data.fee_summary_all) ? data.fee_summary_all : (data.fee_summary || {});

  // Top-line MoM trend — rolls up MTD + last-month totals across whatever the
  // current scope is (mine vs all), so the chip on the Total Referral Fees
  // card mirrors the bar chart logic below. Same accounting rules: skip
  // cancelled archived cases.
  const topTrend = (() => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
    let mtd = 0; let lm = 0;
    for (const c of cases) {
      const f = Number(c.referral_fee || 0);
      if (f <= 0) continue;
      if (c.status === "archived" && !c.referrer_fee_paid) continue;
      const ts = c.created_at ? new Date(c.created_at).getTime() : NaN;
      if (Number.isNaN(ts)) continue;
      if (ts >= thisMonthStart) mtd += f;
      else if (ts >= lastMonthStart) lm += f;
    }
    return { mtd, lm };
  })();

  // Aggregate unread (solicitor → referrer) messages across every referred case.
  // "Unread" = not authored by me and missing my entry in seen_by.
  const refName = referrer?.name;
  const unreadByCase = {};
  let totalUnread = 0;
  let mostRecentUnread = null; // {case_id, created_at, content}
  for (const n of notes || []) {
    const isMine = n.author_role === "referrer" && n.author_name === refName;
    if (isMine) continue;
    const seenByMe = (n.seen_by || []).some((s) => s.actor_name === refName && s.actor_role === "referrer");
    if (seenByMe) continue;
    totalUnread += 1;
    if (!unreadByCase[n.case_id]) unreadByCase[n.case_id] = { count: 0, latest: null };
    unreadByCase[n.case_id].count += 1;
    const slot = unreadByCase[n.case_id];
    if (!slot.latest || (n.created_at || "") > (slot.latest.created_at || "")) {
      slot.latest = n;
    }
    if (!mostRecentUnread || (n.created_at || "") > (mostRecentUnread.created_at || "")) {
      mostRecentUnread = n;
    }
  }
  // Build sortable list for the dropdown — newest activity first
  const unreadCaseRows = Object.entries(unreadByCase)
    .map(([caseId, info]) => {
      const c = cases.find((x) => x.id === caseId);
      return c ? { case: c, count: info.count, latest: info.latest } : null;
    })
    .filter(Boolean)
    .sort((a, b) => (b.latest?.created_at || "").localeCompare(a.latest?.created_at || ""));

  const goToCaseMessages = (caseId) => {
    navigate(`/referrer-dashboard/${token}/case/${caseId}?tab=messages`);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="referrer-dashboard-page">
      <div style={{ background: '#100f5d' }}>
        <div className="h-[3px]" style={{ background: '#c87d3a' }} />
        <div className="max-w-5xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <img src="/logo.png" alt="UKC Legal Solicitors" className="h-9 w-auto hidden sm:block shrink-0" />
            <div className="hidden sm:block w-px h-8 shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }} />
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: '#c87d3a' }}>
              <User weight="bold" className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{referrer.name}</h1>
              <p className="text-xs text-slate-300 truncate">{referrer.company || "Referrer Dashboard"}</p>
              {(() => {
                // Tiny contribution badge: only show once the broker has at
                // least one referred case so brand-new accounts see clean copy.
                const f = data.fee_summary || {};
                const lifetime = f.lifetime_cases || 0;
                if (lifetime === 0) return null;
                const active = f.active_cases || 0;
                const paid = f.fees_paid || 0;
                return (
                  <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid="contribution-summary">
                    <span><span className="text-white font-semibold">{active}</span> active</span>
                    <span className="text-slate-600">·</span>
                    <span><span className="text-white font-semibold">{lifetime}</span> lifetime</span>
                    {paid > 0 && (
                      <>
                        <span className="text-slate-600">·</span>
                        <span><span className="font-semibold" style={{ color: '#c87d3a' }}>{formatCurrency(paid)}</span> paid</span>
                      </>
                    )}
                  </p>
                );
              })()}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Unread messages bell — opens a dropdown listing every case with unread messages */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="unread-bell-btn"
                  className="relative h-10 w-10 rounded-full flex items-center justify-center transition-colors hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.12)' }}
                  aria-label="Unread messages"
                >
                  <Bell weight={totalUnread > 0 ? "fill" : "regular"} className="w-5 h-5" style={{ color: totalUnread > 0 ? '#c87d3a' : '#fff' }} />
                  {totalUnread > 0 && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center ring-2"
                      style={{ ['--tw-ring-color']: '#100f5d' }}
                      data-testid="unread-bell-count"
                    >
                      {totalUnread > 9 ? "9+" : totalUnread}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[360px] p-0" data-testid="unread-bell-dropdown">
                <div className="px-4 py-3 border-b border-slate-100" style={{ background: 'rgba(16,15,93,0.04)' }}>
                  <p className="text-sm font-bold" style={{ color: '#100f5d', fontFamily: 'Outfit, sans-serif' }}>
                    {totalUnread > 0 ? `${totalUnread} new message${totalUnread === 1 ? "" : "s"}` : "No new messages"}
                  </p>
                  {totalUnread > 0 && (
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Across {unreadCaseRows.length} case{unreadCaseRows.length === 1 ? "" : "s"} — click to open the chat
                    </p>
                  )}
                </div>
                <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-100">
                  {unreadCaseRows.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <ChatCircle weight="thin" className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-xs text-slate-400">You're all caught up.</p>
                    </div>
                  ) : (
                    unreadCaseRows.map((row) => (
                      <button
                        key={row.case.id}
                        data-testid={`unread-bell-row-${row.case.id}`}
                        onClick={() => goToCaseMessages(row.case.id)}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 relative" style={{ background: 'rgba(200,125,58,0.12)' }}>
                          <ChatCircle weight="fill" className="w-4 h-4" style={{ color: '#c87d3a' }} />
                          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                            {row.count > 9 ? "9+" : row.count}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 truncate">{row.case.title}</p>
                          {row.case.client_name && <p className="text-[11px] text-slate-500 truncate">{row.case.client_name}</p>}
                          {row.latest?.content && (
                            <p className="text-[11px] text-slate-400 truncate mt-0.5 italic">"{row.latest.content.slice(0, 90)}{row.latest.content.length > 90 ? "…" : ""}"</p>
                          )}
                          {row.latest?.created_at && (
                            <p className="text-[10px] text-slate-400 mt-0.5">{new Date(row.latest.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                          )}
                        </div>
                        <CaretRight weight="bold" className="w-4 h-4 mt-1 shrink-0" style={{ color: '#100f5d' }} />
                      </button>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <a href={`/quote/referrer?ref_name=${encodeURIComponent(referrer.name)}&ref_token=${token}`}
              data-testid="new-quote-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium text-white rounded-lg transition-colors hover:opacity-90 whitespace-nowrap"
              style={{ background: '#c87d3a' }}>
              <CurrencyGbp weight="bold" className="w-3.5 h-3.5" /> New Quote
            </a>
            {/* New Branded Quote — same destination as the embeddable
                bespoke widget link below, opened in a new tab so the
                referrer can fire one off without losing their place
                on the dashboard. Uses the broker's existing access
                token (no extra auth needed). */}
            <a href={`/referrers-web-tool?ref_token=${token}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="new-branded-quote-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium text-white rounded-lg transition-colors hover:opacity-90 whitespace-nowrap"
              style={{ background: '#1462a0' }}>
              <Sparkle weight="bold" className="w-3.5 h-3.5" /> New Branded Quote
            </a>
            {/* Branch settings — gear icon, only visible when this referrer
                admins ≥1 branch. Opens a slide-over with the BranchAdminCard
                UI (branch overrides + member management) so the main
                dashboard stays focused on the broker's own pipeline. */}
            {(data.branch_acl?.branch_admin_of || []).length > 0 && (
              <button
                onClick={() => setBranchSheetOpen(true)}
                data-testid="branch-settings-btn"
                title={`Branch settings · ${(data.branch_acl.branch_admin_of || []).length} branch${(data.branch_acl.branch_admin_of || []).length === 1 ? "" : "es"}`}
                className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-medium text-white/80 rounded-lg transition-colors hover:bg-white/10 hover:text-white relative"
              >
                <Buildings weight="bold" className="w-4 h-4" />
                <span className="hidden sm:inline">Branch settings</span>
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-amber-500 text-[9px] font-bold flex items-center justify-center text-white ring-2 ring-[#100f5d]">
                  {(data.branch_acl.branch_admin_of || []).length}
                </span>
              </button>
            )}
            <div>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{cases.length}</p>
              <p className="text-xs" style={{ color: '#e89e63' }}>referred case{cases.length !== 1 ? "s" : ""}</p>
            </div>
            {/* Sign-out — only meaningful when the referrer arrived via the new
                login flow (we have a JWT in localStorage). For legacy
                magic-link visitors we still show it so they can switch
                accounts on a shared device; clicking just clears any tokens
                and bounces them to /referrer-login. */}
            <button
              onClick={async () => {
                try {
                  if (user) await logout();
                } finally {
                  // Defensive: ensure tokens are gone even if logout() failed
                  // (e.g. backend 401'd because the JWT had already expired).
                  try {
                    localStorage.removeItem("access_token");
                    localStorage.removeItem("refresh_token");
                  } catch { /* noop */ }
                  navigate("/referrer-login");
                }
              }}
              data-testid="referrer-logout-btn"
              title="Sign out"
              className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-medium text-white/80 rounded-lg transition-colors hover:bg-white/10 hover:text-white"
            >
              <SignOut weight="bold" className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {showScopeToggle && (
          <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="scope-toggle">
            <span className="text-xs text-slate-500 mr-1">View:</span>
            <button
              type="button"
              onClick={() => setScope("mine")}
              data-testid="scope-mine-btn"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                effectiveScope === "mine"
                  ? "bg-[#100f5d] text-white border-[#100f5d]"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              Mine
            </button>
            <button
              type="button"
              onClick={() => setScope("all")}
              data-testid="scope-all-btn"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${
                effectiveScope === "all"
                  ? "bg-[#100f5d] text-white border-[#100f5d]"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Buildings weight="bold" className="w-3 h-3" />
              All · {data.company}
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${effectiveScope === "all" ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>
                {companyMembers.length}
              </span>
            </button>
            {/* Branch-ACL footnote — only shows when the authed referrer's
                ACL is narrower than the full company branch list (e.g. they
                only see Norwich + London out of 4). Tells them their "All"
                view is bounded. */}
            {effectiveScope === "all" && (() => {
              const totalBranches = (data.branches || []).length;
              const acl = data.branch_acl || {};
              const allowedCount = (acl.allowed_branches || []).length;
              if (totalBranches <= 1 || allowedCount === 0 || allowedCount >= totalBranches) return null;
              const allowedNames = (acl.allowed_branches || [])
                .map((bid) => (data.branches || []).find((b) => b.id === bid)?.name)
                .filter(Boolean);
              return (
                <span
                  className="text-[11px] text-slate-500 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-100"
                  title={`Visible branches: ${allowedNames.join(", ")}`}
                  data-testid="scope-acl-footnote"
                >
                  <Buildings weight="bold" className="w-3 h-3 text-amber-600" />
                  Bounded to <strong className="text-slate-700">{allowedCount}</strong> of {totalBranches} branches
                </span>
              );
            })()}
          </div>
        )}

        {/* Fee Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="fee-summary">
          <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Total Referral Fees</p>
            <div className="flex items-baseline gap-2 mt-1 flex-wrap">
              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="fee-total-historic">
                {formatCurrency(fee.total_historic || 0)}
              </p>
              {(() => {
                const { mtd, lm } = topTrend;
                if (mtd === 0 && lm === 0) return null;
                if (lm === 0) {
                  return (
                    <span
                      className="text-[10px] font-semibold text-emerald-600 inline-flex items-center gap-0.5"
                      title={`This month: ${formatCurrency(mtd)} · Last month: ${formatCurrency(lm)}`}
                      data-testid="fee-trend-chip"
                    >▲ new</span>
                  );
                }
                const diffPct = Math.round(((mtd - lm) / lm) * 100);
                const isUp = mtd >= lm;
                const clamp = Math.max(-999, Math.min(999, diffPct));
                return (
                  <span
                    className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${isUp ? "text-emerald-600" : "text-rose-600"}`}
                    title={`This month: ${formatCurrency(mtd)} · Last month: ${formatCurrency(lm)}`}
                    data-testid="fee-trend-chip"
                  >
                    {isUp ? "▲" : "▼"} {Math.abs(clamp)}%
                  </span>
                );
              })()}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              {fee.total_cases || 0} case{(fee.total_cases || 0) !== 1 ? "s" : ""} referred
              {fee.cancelled_cases > 0 && (
                <span className="text-slate-400"> · <span className="text-slate-500 font-medium" title="Cases archived without being paid — fee not earned">{fee.cancelled_cases} cancelled</span></span>
              )}
            </p>
          </div>
          <div className="bg-white border border-emerald-200 rounded-lg p-4 shadow-sm">
            <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">Awaiting Payment</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="fee-awaiting-payment">
              {formatCurrency(fee.awaiting_payment || 0)}
            </p>
            <p className="text-xs text-emerald-500 mt-0.5">{fee.completed_cases || 0} completed</p>
          </div>
          <div className="bg-white border border-violet-200 rounded-lg p-4 shadow-sm">
            <p className="text-[10px] font-semibold text-violet-600 uppercase tracking-wide">Paid</p>
            <p className="text-2xl font-bold text-violet-700 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="fee-paid">
              {formatCurrency(fee.fees_paid || 0)}
            </p>
            <p className="text-xs text-violet-500 mt-0.5">{fee.paid_cases || 0} paid</p>
          </div>
          <div className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm">
            <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">Future Fees</p>
            <p className="text-2xl font-bold text-blue-700 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="fee-future">
              {formatCurrency(fee.future_fees || 0)}
            </p>
            <p className="text-xs text-blue-500 mt-0.5">{fee.active_cases || 0} active</p>
          </div>
        </div>

        {/* Usage tile — month-to-date count of quotes THIS referrer
            raised, split by tool (branded bespoke vs standard). Lets
            the broker self-measure engagement without having to scroll
            the quotes list. Hidden when nothing's been generated yet
            this month so we don't show an empty / discouraging tile. */}
        {data.usage_this_month && (data.usage_this_month.branded + data.usage_this_month.standard) > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 flex flex-wrap items-center justify-between gap-3 shadow-sm" data-testid="usage-tile">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: '#0f172a' }}>
                <Sparkle weight="fill" className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  You&apos;ve generated <span className="tabular-nums">{data.usage_this_month.branded + data.usage_this_month.standard}</span> quote{(data.usage_this_month.branded + data.usage_this_month.standard) === 1 ? "" : "s"} in {data.usage_this_month.month_label}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 tabular-nums">
                  <span className="font-medium text-slate-700">{data.usage_this_month.branded}</span> branded
                  {" · "}
                  <span className="font-medium text-slate-700">{data.usage_this_month.standard}</span> standard
                </p>
              </div>
            </div>
            {/* Quick CTA to start another — links to whichever tool the
                broker uses less, gentle nudge to try the underused one. */}
            <a
              href={
                data.usage_this_month.branded >= data.usage_this_month.standard
                  ? `/quote/referrer?ref_name=${encodeURIComponent(referrer.name)}&ref_token=${token}`
                  : `/referrers-web-tool?ref_token=${token}`
              }
              target="_blank"
              rel="noopener noreferrer"
              data-testid="usage-cta"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Try the {data.usage_this_month.branded >= data.usage_this_month.standard ? "standard" : "branded"} tool
              <ArrowSquareOut weight="bold" className="w-3 h-3" />
            </a>
          </div>
        )}

        {/* Branch admin self-service moved into a slide-over sheet — opened
            via the gear button in the header, so the main dashboard stays
            focused on the broker's own pipeline. The card itself short-
            circuits to null when this referrer admins nothing, so passing
            it through unconditionally is safe. */}
        {/* (Old inline card mount intentionally removed — sheet below.) */}

        {/* By-colleague breakdown — only shown on the "All" company view when
            there are ≥2 colleagues. Aggregates referral fees per broker so the
            agency can see who's pulling weight at a glance. Renders nothing
            when there are no fees yet. */}
        {effectiveScope === "all" && companyMembers.length > 1 && (() => {
          // Build per-colleague totals from the unfiltered case list. Skip
          // archived-but-unpaid (cancelled) cases — matches the fee-summary
          // accounting rules already used elsewhere.
          const now = new Date();
          const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
          const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
          const byId = {};
          for (const m of companyMembers) byId[m.id] = { id: m.id, name: m.name, total: 0, cases: 0, mtd: 0, lm: 0 };
          for (const c of rawCases) {
            const fee = Number(c.referral_fee || 0);
            if (fee <= 0) continue;
            if (c.status === "archived" && !c.referrer_fee_paid) continue;
            const rid = c.referrer_id;
            if (!rid || !byId[rid]) continue;
            byId[rid].total += fee;
            byId[rid].cases += 1;
            const ts = c.created_at ? new Date(c.created_at).getTime() : NaN;
            if (!Number.isNaN(ts)) {
              if (ts >= thisMonthStart) byId[rid].mtd += fee;
              else if (ts >= lastMonthStart) byId[rid].lm += fee;
            }
          }
          const rows = Object.values(byId).filter((r) => r.total > 0 || r.cases > 0).sort((a, b) => b.total - a.total);
          if (rows.length === 0) return null;
          const max = Math.max(...rows.map((r) => r.total), 1);
          const grandTotal = rows.reduce((s, r) => s + r.total, 0);
          // Branch totals — only meaningful when ≥2 branches exist in scope
          // AND members are spread across them. Rolls each colleague's total
          // into a branch bucket (branchless colleagues bucket together).
          const branchById = {};
          for (const b of (data.branches || [])) branchById[b.id] = b;
          const branchTotals = {};
          for (const r of rows) {
            const memberBranchId = (companyMembers.find((m) => m.id === r.id) || {}).branch_id || "__none__";
            if (!branchTotals[memberBranchId]) branchTotals[memberBranchId] = { id: memberBranchId, name: memberBranchId === "__none__" ? "(No branch)" : (branchById[memberBranchId]?.name || "Unknown branch"), total: 0, cases: 0 };
            branchTotals[memberBranchId].total += r.total;
            branchTotals[memberBranchId].cases += r.cases;
          }
          const branchRows = Object.values(branchTotals).sort((a, b) => b.total - a.total);
          const branchMax = Math.max(...branchRows.map((b) => b.total), 1);
          const showBranchStrip = branchRows.length >= 2;
          return (
            <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="by-colleague-breakdown">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Buildings weight="bold" className="w-4 h-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    By colleague <span className="text-slate-400 font-normal">· {data.company}</span>
                  </h3>
                </div>
                <span className="text-[11px] text-slate-400">{formatCurrency(grandTotal)} total</span>
              </div>
              {showBranchStrip && (
                <div className="mb-3 pb-3 border-b border-slate-100" data-testid="by-branch-strip">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">By branch</p>
                  <div className="space-y-1.5">
                    {branchRows.map((br) => {
                      const pct = branchMax > 0 ? Math.round((br.total / branchMax) * 100) : 0;
                      return (
                        <div key={br.id} className="flex items-center gap-3" data-testid={`branch-bar-${br.id}`}>
                          <div className="w-32 shrink-0 min-w-0">
                            <p className={`text-xs font-medium truncate ${br.id === "__none__" ? "text-slate-400 italic" : "text-slate-700"}`}>
                              {br.name}
                            </p>
                            <p className="text-[10px] text-slate-400">{br.cases} case{br.cases === 1 ? "" : "s"}</p>
                          </div>
                          <div className="flex-1 h-4 bg-slate-100 rounded-md overflow-hidden">
                            <div className="h-full rounded-md bg-amber-400" style={{ width: `${Math.max(pct, 2)}%` }} />
                          </div>
                          <div className="w-24 text-right shrink-0">
                            <p className="text-xs font-semibold text-slate-900 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              {formatCurrency(br.total)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {rows.map((r) => {
                  const pct = max > 0 ? Math.round((r.total / max) * 100) : 0;
                  const isMe = r.id === referrer.id;
                  // MoM trend: green ▲ when MTD > LM, red ▼ when MTD < LM,
                  // grey – when both are zero. Delta percent uses LM as the
                  // denominator (capped to ±999% so we never overflow the
                  // label). When LM=0 and MTD>0 we surface "new" to avoid
                  // showing a misleading +Infinity%.
                  let trendTag;
                  if (r.mtd === 0 && r.lm === 0) {
                    trendTag = (
                      <span className="text-[10px] text-slate-300" data-testid={`colleague-trend-${r.id}`}>—</span>
                    );
                  } else if (r.lm === 0) {
                    trendTag = (
                      <span
                        className="text-[10px] font-semibold text-emerald-600 inline-flex items-center gap-0.5"
                        title={`This month: ${formatCurrency(r.mtd)} · Last month: ${formatCurrency(r.lm)}`}
                        data-testid={`colleague-trend-${r.id}`}
                      >▲ new</span>
                    );
                  } else {
                    const diffPct = Math.round(((r.mtd - r.lm) / r.lm) * 100);
                    const isUp = r.mtd >= r.lm;
                    const arrow = isUp ? "▲" : "▼";
                    const clamp = Math.max(-999, Math.min(999, diffPct));
                    trendTag = (
                      <span
                        className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${isUp ? "text-emerald-600" : "text-rose-600"}`}
                        title={`This month: ${formatCurrency(r.mtd)} · Last month: ${formatCurrency(r.lm)}`}
                        data-testid={`colleague-trend-${r.id}`}
                      >
                        {arrow} {Math.abs(clamp)}%
                      </span>
                    );
                  }
                  return (
                    <div key={r.id} className="flex items-center gap-3" data-testid={`colleague-bar-${r.id}`}>
                      <div className="w-32 shrink-0 min-w-0">
                        <p className={`text-xs font-medium truncate ${isMe ? "text-slate-900" : "text-slate-600"}`}>
                          {r.name} {isMe && <span className="text-[10px] font-semibold text-indigo-600">· you</span>}
                        </p>
                        <p className="text-[10px] text-slate-400 inline-flex items-center gap-1.5">
                          {r.cases} case{r.cases === 1 ? "" : "s"}
                          {trendTag}
                        </p>
                      </div>
                      <div className="flex-1 h-5 bg-slate-100 rounded-md relative overflow-hidden">
                        <div
                          className={`h-full rounded-md transition-all ${isMe ? "bg-indigo-500" : "bg-slate-400"}`}
                          style={{ width: `${Math.max(pct, 2)}%` }}
                        />
                      </div>
                      <div className="w-24 text-right shrink-0">
                        <p className="text-xs font-semibold text-slate-900 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {formatCurrency(r.total)}
                        </p>
                        <p className="text-[10px] text-slate-400 tabular-nums">{formatCurrency(r.mtd)} MTD</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-3 italic">Trend compares this month vs last month, based on case creation date.</p>
            </div>
          );
        })()}

        {/* Draft quotes indicator — quotes generated but not yet instructed.
            Intentionally subtle: no money shown (they don't count as earnings),
            just a pipeline counter with a one-click jump to the Quotes tab. */}
        {(() => {
          const drafts = quotes.filter((q) => !q.linked_case_id && !q.instructed && !q.client_instructed);
          if (drafts.length === 0) return null;
          const totalDraftValue = drafts.reduce((s, q) => s + (q.referral_fee || 0), 0);
          return (
            <button
              type="button"
              onClick={() => setActiveTab("quotes")}
              className="mb-6 w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
              data-testid="draft-quotes-chip"
              title="Open the Quotes tab"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <p className="text-xs text-slate-600">
                  <span className="font-semibold text-slate-900">{drafts.length}</span> draft quote{drafts.length === 1 ? "" : "s"}
                  <span className="text-slate-400"> · not yet instructed</span>
                  {totalDraftValue > 0 && (
                    <span className="text-slate-400 hidden sm:inline"> · {formatCurrency(totalDraftValue)} potential</span>
                  )}
                </p>
              </div>
              <CaretRight weight="bold" className="w-3.5 h-3.5 text-slate-400" />
            </button>
          );
        })()}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white border border-slate-200 rounded-lg p-1 w-fit flex-wrap">
          {[
            { key: "cases", label: `Cases (${cases.length})`, icon: FolderSimple },
            { key: "quotes", label: `Quotes (${quotes.length})`, icon: CurrencyGbp },
            { key: "invoices", label: `Invoices (${invoices.length})`, icon: Receipt },
            { key: "documents", label: `Documents (${documents.length})`, icon: FileText },
            { key: "profile", label: "Profile", icon: Bank },
          ].map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeTab === tab.key ? "text-white" : "text-slate-500 hover:text-slate-700"
              }`}
              style={activeTab === tab.key ? { background: '#100f5d' } : {}}>
              <tab.icon weight={activeTab === tab.key ? "fill" : "regular"} className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Cases Tab */}
        {activeTab === "cases" && (
          <div className="space-y-3" data-testid="cases-list">
            {cases.length === 0 ? (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
                <FolderSimple weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No referred cases yet</p>
              </div>
            ) : cases.map((c) => {
              const caseNotes = notes.filter((n) => n.case_id === c.id);
              const caseNoteCount = caseNotes.length;
              const caseDocCount = documents.filter((d) => d.case_id === c.id).length;
              const refName = data?.referrer?.name;
              const unreadCount = caseNotes.filter((n) => {
                // Only count messages NOT authored by me
                const isMine = n.author_role === "referrer" && n.author_name === refName;
                if (isMine) return false;
                const seenByMe = (n.seen_by || []).some((s) => s.actor_name === refName && s.actor_role === "referrer");
                return !seenByMe;
              }).length;
              return (
                <button key={c.id}
                  onClick={() => navigate(`/referrer-dashboard/${token}/case/${c.id}`)}
                  className="w-full bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden text-left hover:shadow-md hover:border-[#100f5d]/30 transition-all"
                  data-testid={`case-${c.id}`}>
                  <div className="p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 relative ${
                        c.progress === 100 ? "bg-emerald-100" : ""
                      }`} style={c.progress !== 100 ? { background: 'rgba(16,15,93,0.08)' } : {}}>
                        {c.progress === 100
                          ? <Check weight="bold" className="w-5 h-5 text-emerald-600" />
                          : <Clock weight="bold" className="w-5 h-5" style={{ color: '#100f5d' }} />}
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white" data-testid={`unread-badge-${c.id}`}>
                            {unreadCount > 9 ? "9+" : unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                          {c.title}
                          {c._company_view && (
                            <span
                              className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                              title={`Referred by ${c._raised_by_name || "a colleague"} at your company`}
                              data-testid={`case-colleague-pill-${c.id}`}
                            >
                              via {c._raised_by_name || "colleague"}
                            </span>
                          )}
                          {unreadCount > 0 && (
                            <span className="text-[10px] font-semibold text-red-600 normal-case">· new message{unreadCount === 1 ? "" : "s"}</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{c.property_address} · {c.client_name}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-400">
                          <span className="inline-flex items-center gap-1"><ChatCircle weight="bold" className="w-3 h-3" />{caseNoteCount}</span>
                          <span className="inline-flex items-center gap-1"><FileText weight="bold" className="w-3 h-3" />{caseDocCount}</span>
                          <span className="capitalize">{c.case_type}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <p className="text-sm font-bold" style={{ color: '#100f5d', fontFamily: 'Outfit, sans-serif' }}>{c.progress}%</p>
                        {c.referral_fee > 0 && <p className="text-[10px] font-medium" style={{ color: '#c87d3a' }}>{formatCurrency(c.referral_fee)}</p>}
                        {c.referrer_fee_paid && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Paid</span>}
                      </div>
                      <CaretRight className="w-4 h-4 text-slate-300" weight="bold" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === "quotes" && (
          <div data-testid="quotes-list">
            {quotes.length === 0 ? (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
                <CurrencyGbp weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No quotes linked</p>
              </div>
            ) : (() => {
              const q = quoteSearch.trim().toLowerCase();
              const filtered = q
                ? quotes.filter((x) =>
                    (x.client_name || "").toLowerCase().includes(q) ||
                    (x.property_address || "").toLowerCase().includes(q) ||
                    (x.property_postcode || "").toLowerCase().includes(q) ||
                    (x.transaction_type || "").toLowerCase().includes(q)
                  )
                : quotes;
              return (
                <>
                  <div className="relative mb-3 flex items-center gap-2">
                    <div className="relative flex-1">
                      <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        data-testid="referrer-quote-search"
                        placeholder="Search by client, address, postcode, or transaction type…"
                        value={quoteSearch}
                        onChange={(e) => setQuoteSearch(e.target.value)}
                        className="pl-10 h-10 bg-white"
                      />
                    </div>
                    {(() => {
                      const deletableInView = filtered.filter(isQuoteDeletable);
                      const allSelected = deletableInView.length > 0 && deletableInView.every((x) => selectedQuoteIds.has(x.id));
                      return (
                        <button
                          type="button"
                          disabled={deletableInView.length === 0}
                          onClick={() => {
                            if (allSelected) {
                              setSelectedQuoteIds((prev) => {
                                const next = new Set(prev);
                                deletableInView.forEach((x) => next.delete(x.id));
                                return next;
                              });
                            } else {
                              setSelectedQuoteIds((prev) => {
                                const next = new Set(prev);
                                deletableInView.forEach((x) => next.add(x.id));
                                return next;
                              });
                            }
                          }}
                          className="h-10 shrink-0 inline-flex items-center gap-2 px-3 rounded-lg border text-xs font-semibold transition-all bg-white border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid="referrer-quotes-select-all"
                          title={deletableInView.length === 0 ? "No deletable quotes in view" : allSelected ? "Clear selection" : "Select all deletable quotes in view"}
                        >
                          {allSelected ? "Clear selection" : `Select all (${deletableInView.length})`}
                        </button>
                      );
                    })()}
                  </div>

                  {selectedQuoteIds.size > 0 && (
                    <div className="mb-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border" style={{ borderColor: "rgba(220,38,38,0.25)", background: "rgba(254,242,242,0.85)" }} data-testid="referrer-quotes-bulk-bar">
                      <p className="text-xs font-semibold text-red-700">
                        {selectedQuoteIds.size} quote{selectedQuoteIds.size === 1 ? "" : "s"} selected
                      </p>
                      <div className="flex items-center gap-2">
                        <Button onClick={() => setSelectedQuoteIds(new Set())} variant="outline" size="sm" className="h-8 text-xs" data-testid="referrer-quotes-cancel-selection">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleBulkDeleteQuotes}
                          disabled={deletingQuotes}
                          size="sm"
                          className="h-8 text-xs gap-1.5 bg-red-600 hover:bg-red-700 text-white"
                          data-testid="referrer-quotes-bulk-delete-btn"
                        >
                          <Trash weight="bold" className="w-3.5 h-3.5" />
                          {deletingQuotes ? "Deleting…" : `Delete ${selectedQuoteIds.size}`}
                        </Button>
                      </div>
                    </div>
                  )}

                  {filtered.length === 0 ? (
                    <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="no-quote-matches">
                      <MagnifyingGlass weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">No quotes match "{quoteSearch}"</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filtered.map((quote) => {
                        const selected = selectedQuoteIds.has(quote.id);
                        const deletable = isQuoteDeletable(quote);
                        return (
                          <div
                            key={quote.id}
                            className={`relative bg-white border rounded-lg shadow-sm transition-all ${selected ? "border-red-300 ring-2 ring-red-100" : "border-slate-200 hover:border-[#100f5d] hover:shadow-md"}`}
                            data-testid={`quote-row-${quote.id}`}
                          >
                            <div className="flex items-stretch">
                              {/* Checkbox column — only deletable quotes get one */}
                              <label className={`flex items-center px-3 ${deletable ? "cursor-pointer" : "cursor-not-allowed opacity-30"}`} title={deletable ? "Select for bulk delete" : "Already converted to a case"}>
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  disabled={!deletable}
                                  onChange={() => deletable && toggleQuoteSelection(quote.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`quote-checkbox-${quote.id}`}
                                  className="rounded border-slate-300 w-4 h-4"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() => window.open(`/quote/view/${quote.id}`, "_blank", "noopener,noreferrer")}
                                className="flex-1 text-left p-4 group min-w-0"
                                data-testid={`quote-${quote.id}`}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-[#100f5d]">{quote.client_name}</p>
                                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{quote.transaction_type}</span>
                                      {quote.instructed && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Instructed</span>}
                                      {!quote.instructed && quote.client_instructed && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Requested</span>}
                                      {/* Solicitor-team lifecycle mirrors — the referrer can
                                          see whether their submitted lead has been contacted,
                                          followed up, or has died. Display-only for the
                                          referrer (they don't toggle solicitor workflow). */}
                                      {quote.contacted_at && !quote.instructed && (
                                        <span
                                          className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700"
                                          data-testid={`referrer-quote-contacted-${quote.id}`}
                                          title={`Contacted on ${new Date(quote.contacted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
                                        >
                                          Contacted
                                        </span>
                                      )}
                                      {(quote.follow_up_count > 0) && !quote.instructed && (
                                        <span
                                          className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-100 text-sky-700"
                                          data-testid={`referrer-quote-followed-up-${quote.id}`}
                                          title={
                                            quote.last_followed_up_at
                                              ? `Last chased on ${new Date(quote.last_followed_up_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                                              : "Chased"
                                          }
                                        >
                                          Followed up {quote.follow_up_count}×
                                        </span>
                                      )}
                                      {quote.not_proceeding && (
                                        <span
                                          className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-100 text-rose-700"
                                          data-testid={`referrer-quote-not-proceeding-${quote.id}`}
                                          title={
                                            quote.not_proceeding_reason
                                              ? `Not proceeding — ${quote.not_proceeding_reason}`
                                              : "Not proceeding"
                                          }
                                        >
                                          Not proceeding
                                        </span>
                                      )}
                                      {quote._company_view && (
                                        <span
                                          className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                                          title={`Raised by ${quote._raised_by_name || quote.referrer_name || "a colleague"} at your company`}
                                          data-testid={`quote-colleague-pill-${quote.id}`}
                                        >
                                          via {quote._raised_by_name || quote.referrer_name || "colleague"}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                                      {quote.property_address ? `${quote.property_address} · ` : ""}{formatCurrency(quote.property_value)} · {quote.property_type} · {new Date(quote.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                    </p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{formatCurrency(quote.total)}</p>
                                    {quote.referral_fee > 0 && <p className="text-[10px] font-medium" style={{ color: "#c87d3a" }}>Your fee: {formatCurrency(quote.referral_fee)}</p>}
                                  </div>
                                  <ArrowSquareOut weight="bold" className="w-4 h-4 shrink-0" style={{ color: "#100f5d" }} />
                                </div>
                              </button>
                              <div className="flex items-center gap-0.5 pr-3">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Route to the same tool the quote was generated from:
                                    //   - referrer_bespoke → bespoke web tool (token-gated, custom fees apply)
                                    //   - referrer (legacy) → legacy referrer tool (free-text name, no token)
                                    //   - public → public tool
                                    let url;
                                    if (quote.source === "referrer_bespoke") {
                                      url = `/referrers-web-tool?ref_token=${token}&prefill_quote_id=${quote.id}`;
                                    } else if (quote.source === "referrer") {
                                      const refName = encodeURIComponent(quote.referrer_name || referrer?.name || "");
                                      url = `/quote/referrer?ref_name=${refName}&prefill_quote_id=${quote.id}`;
                                    } else {
                                      url = `/quote?prefill_quote_id=${quote.id}`;
                                    }
                                    window.open(url, "_blank", "noopener,noreferrer");
                                  }}
                                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                  data-testid={`regenerate-quote-${quote.id}`}
                                  title={`Re-generate this quote (opens the ${quote.source === "referrer_bespoke" ? "bespoke" : quote.source === "referrer" ? "legacy referrer" : "public"} tool pre-filled)`}
                                  aria-label="Re-generate quote"
                                >
                                  <ArrowsClockwise weight="bold" className="w-4 h-4" />
                                </button>
                                {deletable ? (
                                  <button
                                    type="button"
                                    onClick={(e) => handleDeleteSingleQuote(quote, e)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    data-testid={`delete-quote-${quote.id}`}
                                    title="Delete quote"
                                    aria-label="Delete quote"
                                  >
                                    <Trash weight="bold" className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <span className="w-8 h-8" aria-hidden="true" />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div data-testid="documents-list">
            {documents.length === 0 ? (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
                <FileText weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No documents uploaded</p>
              </div>
            ) : (
              <>
                {(() => {
                  const ownDocs = documents.filter(isDocOwn);
                  const allOwnSelected = ownDocs.length > 0 && ownDocs.every((d) => selectedDocIds.has(d.id));
                  return (
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="text-xs text-slate-500">
                        {documents.length} document{documents.length === 1 ? "" : "s"} ·
                        {" "}<span className="font-medium text-slate-700">{ownDocs.length}</span> uploaded by you
                      </p>
                      <button
                        type="button"
                        disabled={ownDocs.length === 0}
                        onClick={() => {
                          if (allOwnSelected) {
                            setSelectedDocIds((prev) => {
                              const next = new Set(prev);
                              ownDocs.forEach((d) => next.delete(d.id));
                              return next;
                            });
                          } else {
                            setSelectedDocIds((prev) => {
                              const next = new Set(prev);
                              ownDocs.forEach((d) => next.add(d.id));
                              return next;
                            });
                          }
                        }}
                        className="h-9 inline-flex items-center gap-2 px-3 rounded-lg border text-xs font-semibold transition-all bg-white border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="referrer-docs-select-all"
                      >
                        {allOwnSelected ? "Clear selection" : `Select all my uploads (${ownDocs.length})`}
                      </button>
                    </div>
                  );
                })()}

                {selectedDocIds.size > 0 && (
                  <div className="mb-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border" style={{ borderColor: "rgba(220,38,38,0.25)", background: "rgba(254,242,242,0.85)" }} data-testid="referrer-docs-bulk-bar">
                    <p className="text-xs font-semibold text-red-700">
                      {selectedDocIds.size} document{selectedDocIds.size === 1 ? "" : "s"} selected
                    </p>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => setSelectedDocIds(new Set())} variant="outline" size="sm" className="h-8 text-xs" data-testid="referrer-docs-cancel-selection">
                        Cancel
                      </Button>
                      <Button
                        onClick={handleBulkDeleteDocs}
                        disabled={deletingDocs}
                        size="sm"
                        className="h-8 text-xs gap-1.5 bg-red-600 hover:bg-red-700 text-white"
                        data-testid="referrer-docs-bulk-delete-btn"
                      >
                        <Trash weight="bold" className="w-3.5 h-3.5" />
                        {deletingDocs ? "Deleting…" : `Delete ${selectedDocIds.size}`}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {documents.map((d) => {
                    const caseTitle = cases.find(c => c.id === d.case_id)?.title || d.case_id;
                    const own = isDocOwn(d);
                    const selected = selectedDocIds.has(d.id);
                    return (
                      <div
                        key={d.id}
                        className={`bg-white border rounded-lg p-3 shadow-sm flex items-center gap-3 transition-all ${selected ? "border-red-300 ring-2 ring-red-100" : "border-slate-200"}`}
                        data-testid={`referrer-doc-row-${d.id}`}
                      >
                        <label className={`flex items-center ${own ? "cursor-pointer" : "cursor-not-allowed opacity-30"}`} title={own ? "Select for bulk delete" : "Uploaded by someone else — you can't delete this"}>
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={!own}
                            onChange={() => own && toggleDocSelection(d.id)}
                            data-testid={`doc-checkbox-${d.id}`}
                            className="rounded border-slate-300 w-4 h-4"
                          />
                        </label>
                        <FileText weight="fill" className="w-5 h-5 text-blue-600 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">{d.original_filename}</p>
                          <p className="text-xs text-slate-500">
                            {d.uploaded_by} · {caseTitle} · {new Date(d.created_at).toLocaleDateString("en-GB")}
                            {!own && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-medium">Not yours</span>}
                          </p>
                        </div>
                        {own ? (
                          <button
                            type="button"
                            onClick={() => handleDeleteSingleDoc(d)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                            data-testid={`delete-doc-${d.id}`}
                            title="Delete this document"
                            aria-label="Delete document"
                          >
                            <Trash weight="bold" className="w-4 h-4" />
                          </button>
                        ) : (
                          <span className="w-8 h-8" aria-hidden="true" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div data-testid="invoices-list">
            {invoices.length === 0 ? (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
                <Receipt weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No invoices yet</p>
                <p className="text-xs text-slate-400 mt-1">Invoices are auto-generated when a case completes.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {invoices.map((inv) => (
                  <div key={inv.id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm flex items-center justify-between" data-testid={`invoice-${inv.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Receipt weight="fill" className="w-5 h-5 text-violet-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-mono font-semibold text-slate-900 flex items-center gap-2">
                          {inv.invoice_number}
                          {inv._company_view && (
                            <span
                              className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                              title={`Issued for a case referred by ${inv._raised_by_name || "a colleague"} at your company`}
                              data-testid={`invoice-colleague-pill-${inv.id}`}
                            >
                              via {inv._raised_by_name || "colleague"}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {inv.case_title} · Issued {new Date(inv.issued_at).toLocaleDateString("en-GB")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900">{formatCurrency(inv.amount_gross)}</p>
                        {inv.vat_applied && <p className="text-[10px] text-slate-400">incl. VAT</p>}
                      </div>
                      <span className={`text-[10px] font-medium px-2 py-1 rounded-md ${
                        inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                      }`} data-testid={`invoice-status-${inv.id}`}>
                        {inv.status === "paid" ? `Paid ${inv.paid_at ? new Date(inv.paid_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}` : "Issued"}
                      </span>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const resp = await API.get(`/invoices/${inv.id}/pdf`, { responseType: "blob", headers });
                            const url = URL.createObjectURL(resp.data);
                            const a = document.createElement("a");
                            a.href = url; a.download = `${inv.invoice_number}.pdf`;
                            a.click(); URL.revokeObjectURL(url);
                          } catch { toast.error("Failed to download invoice"); }
                        }}
                        data-testid={`download-invoice-${inv.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-slate-200 rounded-md hover:bg-slate-50 transition-colors">
                        <DownloadSimple weight="bold" className="w-3.5 h-3.5" /> PDF
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile / Payment Details Tab */}
        {activeTab === "profile" && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-5" data-testid="profile-tab">
            <div>
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Bank weight="bold" className="w-4 h-4 text-violet-600" /> Payment Details
              </h2>
              <p className="text-xs text-slate-500 mt-1">Used by the solicitor to pay your referral fees. Visible only to the solicitor and printed on case reports.</p>
              {referrer.company && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentOverride(!paymentOverride)}
                    data-testid="payment-override-toggle"
                    className={`text-[11px] font-medium px-2.5 py-1.5 rounded-md border inline-flex items-center gap-1.5 transition-colors ${
                      paymentOverride
                        ? "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                        : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                    }`}
                    title={paymentOverride
                      ? "Click to follow your company's shared payment details"
                      : "Click to use your own payment details (won't affect colleagues)"}
                  >
                    <Bank weight="bold" className="w-3 h-3" />
                    {paymentOverride
                      ? <>Using <strong className="font-semibold">my own</strong> details — click to follow {referrer.company}</>
                      : <>Shared with everyone at <strong className="font-semibold">{referrer.company}</strong> — click to override</>}
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">Bank name</label>
                <Input data-testid="bank-name-input" value={profileForm.bank_name} placeholder="e.g., Barclays"
                  onChange={(e) => setProfileForm({ ...profileForm, bank_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">Account name</label>
                <Input data-testid="account-name-input" value={profileForm.account_name} placeholder="e.g., ABC Estates Ltd"
                  onChange={(e) => setProfileForm({ ...profileForm, account_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">Sort code</label>
                <Input data-testid="sort-code-input" value={profileForm.sort_code} placeholder="00-00-00" maxLength={8}
                  onChange={(e) => setProfileForm({ ...profileForm, sort_code: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">Account number</label>
                <Input data-testid="account-number-input" value={profileForm.account_number} placeholder="12345678" maxLength={10}
                  onChange={(e) => setProfileForm({ ...profileForm, account_number: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">VAT number <span className="text-slate-400">(optional)</span></label>
                <Input data-testid="vat-number-input" value={profileForm.vat_number} placeholder="GB123456789"
                  onChange={(e) => setProfileForm({ ...profileForm, vat_number: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">Payment notes <span className="text-slate-400">(optional)</span></label>
              <Textarea data-testid="payment-notes-input" value={profileForm.payment_notes} placeholder="e.g., Reference: ABC-123"
                rows={2}
                onChange={(e) => setProfileForm({ ...profileForm, payment_notes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <p className="text-[11px] text-slate-400">
                {referrer.payment_details_updated_at
                  ? `Last updated ${new Date(referrer.payment_details_updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                  : "Not yet provided"}
              </p>
              <Button onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-profile-btn"
                className="h-9 px-4 text-white text-xs hover:opacity-90"
                style={{ background: '#100f5d' }}>
                {savingProfile
                  ? "Saving..."
                  : referrer.company
                    ? (paymentOverride ? "Save (my override)" : "Save & apply to company")
                    : "Save Payment Details"}
              </Button>
            </div>
          </div>
        )}

        {/* Self-serve branding (collocated with Profile tab) */}
        {activeTab === "profile" && (
          <SelfBrandingCard token={token} referrer={referrer} onChanged={fetchData} />
        )}
      </div>

      {/* Slide-over for Branch settings — moved off the main dashboard so the
          broker's pipeline stays the focus. Mounted at page root so the
          backdrop covers everything when open. */}
      <Sheet open={branchSheetOpen} onOpenChange={setBranchSheetOpen}>
        <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto" data-testid="branch-settings-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Buildings weight="bold" className="w-4 h-4 text-indigo-600" />
              Branch settings
            </SheetTitle>
            <SheetDescription className="text-xs">
              Manage the branches you admin — addresses, payment overrides, and which colleagues sit where.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {branchSheetOpen && <BranchAdminCard token={token} />}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}


// ==================== Self-serve branding card ====================
// Shown inside the referrer's Profile tab. Lets the referrer upload their own
// logo and pick brand colours without involving the firm. Authenticated via
// `x-referrer-token` against /api/referrers/{id}/branding + /logo.
function SelfBrandingCard({ token, referrer, onChanged }) {
  const headers = { "x-referrer-token": token };
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [form, setForm] = useState({
    use_brand: false,
    brand_primary: "",
    brand_accent: "",
    brand_text_on_primary: "",
    brand_modal_title: "",
    brand_logo_url: "",
    brand_footer_text: "",
    brand_terms_url: "",
  });
  const [brandOverride, setBrandOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useState({ current: null })[0];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get(`/referrers/${referrer.id}/branding`, { headers });
        if (cancelled) return;
        setForm({
          use_brand: !!data.use_brand,
          brand_primary: data.brand_primary || "",
          brand_accent: data.brand_accent || "",
          brand_text_on_primary: data.brand_text_on_primary || "",
          brand_modal_title: data.brand_modal_title || "",
          brand_logo_url: data.brand_logo_url || "",
          brand_footer_text: data.brand_footer_text || "",
          brand_terms_url: data.brand_terms_url || "",
        });
        setBrandOverride(!!data.brand_override);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load branding");
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referrer.id, token]);

  if (loading) {
    return <div className="bg-white border border-slate-200 rounded-lg p-6 mt-5 text-sm text-slate-400">Loading branding…</div>;
  }

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const isHex = (v) => /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(v || "");

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put(`/referrers/${referrer.id}/branding`, {
        use_brand: !!form.use_brand,
        brand_primary: form.brand_primary.trim(),
        brand_accent: form.brand_accent.trim(),
        brand_text_on_primary: form.brand_text_on_primary.trim(),
        brand_modal_title: form.brand_modal_title.trim(),
        brand_footer_text: form.brand_footer_text.trim(),
        brand_terms_url: form.brand_terms_url.trim(),
        override: brandOverride,
      }, { headers });
      toast.success(brandOverride
        ? "Saved as your personal branding"
        : (referrer.company ? "Saved as company branding — applied to colleagues" : "Branding saved"));
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be 2MB or smaller"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Logo must be an image"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post(`/referrers/${referrer.id}/logo`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      set("brand_logo_url", data.brand_logo_url);
      toast.success("Logo uploaded");
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleLogoClear = async () => {
    try {
      await API.delete(`/referrers/${referrer.id}/logo`, { headers });
      set("brand_logo_url", "");
      toast.success("Logo removed");
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear logo");
    }
  };

  const previewPrimary = isHex(form.brand_primary) ? form.brand_primary : "#100f5d";
  const previewAccent = isHex(form.brand_accent) ? form.brand_accent : "#c87d3a";
  const previewText = isHex(form.brand_text_on_primary) ? form.brand_text_on_primary : "#ffffff";
  const logoSrc = form.brand_logo_url
    ? (form.brand_logo_url.startsWith("/") ? `${origin}${form.brand_logo_url}` : form.brand_logo_url)
    : "/logo.png";

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-5 mt-5" data-testid="self-branding-card">
      <div>
        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Palette weight="bold" className="w-4 h-4 text-violet-600" /> Your Branding
        </h2>
        <p className="text-xs text-slate-500 mt-1">Customise the look of the Bespoke Quote Tool that visitors of your website see. Leave colours blank to inherit UKC defaults.</p>
        {referrer.company && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setBrandOverride(!brandOverride)}
              data-testid="brand-override-toggle"
              className={`text-[11px] font-medium px-2.5 py-1.5 rounded-md border inline-flex items-center gap-1.5 transition-colors ${
                brandOverride
                  ? "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                  : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
              }`}
              title={brandOverride
                ? "Click to follow your company's shared branding"
                : "Click to use your own branding (won't affect colleagues)"}
            >
              <Palette weight="bold" className="w-3 h-3" />
              {brandOverride
                ? <>Using <strong className="font-semibold">my own</strong> branding — click to follow {referrer.company}</>
                : <>Shared with everyone at <strong className="font-semibold">{referrer.company}</strong> — click to override</>}
            </button>
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.use_brand}
          onChange={(e) => set("use_brand", e.target.checked)}
          className="rounded border-slate-300"
          data-testid="self-brand-use-cb"
        />
        <span><span className="font-semibold">Apply my branding</span> — when off, falls back to UKC defaults.</span>
      </label>

      {/* Logo */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Logo</h4>
        <p className="text-[11px] text-slate-500 -mt-1">Max 2MB · PNG / JPG / SVG / WebP. White-on-transparent works best on dark backgrounds.</p>
        <div className="flex items-center gap-3">
          <div className="h-14 w-32 rounded-md flex items-center justify-center overflow-hidden border border-slate-200" style={{ background: previewPrimary }}>
            <img src={logoSrc} alt="logo preview" className="max-h-12 max-w-full object-contain" data-testid="self-brand-logo-preview" onError={(e) => { e.currentTarget.src = "/logo.png"; }} />
          </div>
          <div className="flex flex-col gap-1.5">
            <input ref={(el) => (fileInputRef.current = el)} type="file" accept="image/*" hidden onChange={(e) => handleLogoUpload(e.target.files?.[0])} data-testid="self-brand-logo-input" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="self-brand-upload-btn">
              <ImageIcon weight="bold" className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload logo"}
            </Button>
            {form.brand_logo_url && (
              <Button onClick={handleLogoClear} variant="ghost" size="sm" className="h-7 text-[11px] text-red-600 hover:text-red-700 gap-1" data-testid="self-brand-remove-logo-btn">
                <XIcon weight="bold" className="w-3 h-3" /> Remove logo
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Colours */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Colours</h4>
        <p className="text-[11px] text-slate-500 -mt-1">Pick a hex colour, or leave blank to inherit. Accent is used for the thin top stripe and small dots.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SelfColorField label="Primary (header)" value={form.brand_primary} onChange={(v) => set("brand_primary", v)} fallback="#100f5d" testId="self-brand-primary" />
          <SelfColorField label="Accent (stripe)" value={form.brand_accent} onChange={(v) => set("brand_accent", v)} fallback="#c87d3a" testId="self-brand-accent" />
          <SelfColorField label="Text on primary" value={form.brand_text_on_primary} onChange={(v) => set("brand_text_on_primary", v)} fallback="#ffffff" testId="self-brand-text" />
        </div>
      </div>

      {/* Modal title */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-slate-700">Embedded modal title</label>
        <Input
          value={form.brand_modal_title}
          onChange={(e) => set("brand_modal_title", e.target.value)}
          placeholder={`${referrer.company || referrer.name} · Instant Quote`}
          maxLength={80}
          data-testid="self-brand-modal-title-input"
        />
        <p className="text-[10px] text-slate-400">Shown across the top of the embedded modal on your website. Leave blank for "{referrer.company || referrer.name} · Instant Quote".</p>
      </div>

      {/* Quote PDF & share-page footer overrides */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-slate-700">PDF / share footer text</label>
        <Input
          value={form.brand_footer_text}
          onChange={(e) => set("brand_footer_text", e.target.value)}
          placeholder="Auto: company · address · phone · email · VAT"
          maxLength={240}
          data-testid="self-brand-footer-text-input"
        />
        <p className="text-[10px] text-slate-400">
          Replaces the small line at the bottom of every quote PDF and share page. Leave blank to auto-compose from your contact details. Use this for company number / regulated-by lines etc.
        </p>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-slate-700">Terms &amp; Conditions URL</label>
        <Input
          value={form.brand_terms_url}
          onChange={(e) => set("brand_terms_url", e.target.value)}
          placeholder="https://yourcompany.com/terms"
          type="url"
          data-testid="self-brand-terms-url-input"
        />
        <p className="text-[10px] text-slate-400">
          When set, a "Terms &amp; Conditions" link is added to the quote PDF footer and share page.
        </p>
      </div>

      {/* Live preview */}
      <div className="rounded-lg overflow-hidden border border-slate-200" data-testid="self-brand-preview">
        <div className="h-[3px]" style={{ background: previewAccent }} />
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: previewPrimary }}>
          <img src={logoSrc} alt="logo" className="h-8 max-w-[140px] object-contain" onError={(e) => { e.currentTarget.src = "/logo.png"; }} />
          <span className="text-xs font-semibold" style={{ color: previewText }}>Your Conveyancing Quote</span>
        </div>
        <div className="bg-white px-4 py-3 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: `${previewPrimary}14`, color: previewPrimary }}>
            <span className="w-1 h-1 rounded-full" style={{ background: previewAccent }} /> Bespoke Quote Tool
          </span>
          <span className="text-xs font-bold" style={{ color: previewPrimary }}>Estimated Total · £1,250</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <a
          href={`${origin}/referrers-web-tool?ref_token=${referrer.access_token}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium hover:underline inline-flex items-center gap-1"
          style={{ color: '#100f5d' }}
          data-testid="self-brand-open-tool-link"
        >
          <ArrowSquareOut weight="bold" className="w-3 h-3" /> View live quote tool
        </a>
        <Button onClick={handleSave} disabled={saving} data-testid="save-self-brand-btn"
          className="h-9 px-4 text-white text-xs hover:opacity-90"
          style={{ background: '#100f5d' }}>
          {saving ? "Saving..." : "Save Branding"}
        </Button>
      </div>
    </div>
  );
}

function SelfColorField({ label, value, onChange, fallback, testId }) {
  const display = /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(value || "") ? value : fallback;
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-slate-600">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={display}
          onChange={(e) => onChange(e.target.value)}
          className="w-9 h-9 rounded border border-slate-200 cursor-pointer shrink-0 bg-white"
          data-testid={`${testId}-picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Inherits ${fallback}`}
          className="h-9 text-xs font-mono"
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
}

